import log from 'loglevel';

// Configure logger based on environment
const isDevelopment = process.env.NODE_ENV === 'development';

// Set default log level
if (isDevelopment) {
  log.setLevel('debug'); // Show all logs in development
} else {
  log.setLevel('warn'); // Only show warnings and errors in production
}

// Create logger interface with consistent formatting
export const logger = {
  // Debug: Detailed information for debugging
  debug: (message: string, ...args: any[]) => {
    log.debug(`[DEBUG] ${message}`, ...args);
  },

  // Info: General application flow information
  info: (message: string, ...args: any[]) => {
    log.info(`[INFO] ${message}`, ...args);
  },

  // Warn: Something unexpected but not necessarily an error
  warn: (message: string, ...args: any[]) => {
    log.warn(`[WARN] ${message}`, ...args);
  },

  // Error: Error conditions that should be investigated
  error: (message: string, ...args: any[]) => {
    log.error(`[ERROR] ${message}`, ...args);
  },

  // Component-specific loggers for better organization
  pixi: {
    debug: (message: string, ...args: any[]) => log.debug(`[PIXI] ${message}`, ...args),
    info: (message: string, ...args: any[]) => log.info(`[PIXI] ${message}`, ...args),
    warn: (message: string, ...args: any[]) => log.warn(`[PIXI] ${message}`, ...args),
    error: (message: string, ...args: any[]) => log.error(`[PIXI] ${message}`, ...args),
  },

  interaction: {
    debug: (message: string, ...args: any[]) => log.debug(`[INTERACTION] ${message}`, ...args),
    info: (message: string, ...args: any[]) => log.info(`[INTERACTION] ${message}`, ...args),
    warn: (message: string, ...args: any[]) => log.warn(`[INTERACTION] ${message}`, ...args),
    error: (message: string, ...args: any[]) => log.error(`[INTERACTION] ${message}`, ...args),
  },

  storage: {
    debug: (message: string, ...args: any[]) => log.debug(`[STORAGE] ${message}`, ...args),
    info: (message: string, ...args: any[]) => log.info(`[STORAGE] ${message}`, ...args),
    warn: (message: string, ...args: any[]) => log.warn(`[STORAGE] ${message}`, ...args),
    error: (message: string, ...args: any[]) => log.error(`[STORAGE] ${message}`, ...args),
  },

  bundle: {
    debug: (message: string, ...args: any[]) => log.debug(`[BUNDLE] ${message}`, ...args),
    info: (message: string, ...args: any[]) => log.info(`[BUNDLE] ${message}`, ...args),
    warn: (message: string, ...args: any[]) => log.warn(`[BUNDLE] ${message}`, ...args),
    error: (message: string, ...args: any[]) => log.error(`[BUNDLE] ${message}`, ...args),
  },

  // Runtime log level control
  setLevel: (level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent') => {
    log.setLevel(level);
  },

  // Get current log level
  getLevel: () => {
    return log.getLevel();
  }
};

// Export for direct use if needed
export default logger;

// Add global window method for runtime log level control (development only)
if (isDevelopment && typeof window !== 'undefined') {
  (window as any).setLogLevel = (level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent') => {
    logger.setLevel(level);
    logger.info(`Log level set to: ${level}`);
  };
  
  // Add help function
  (window as any).logHelp = () => {
    console.log(`
Available log levels:
- trace: Most verbose
- debug: Debug information  
- info: General information
- warn: Warnings
- error: Errors only
- silent: No logs

Usage: setLogLevel('debug')
Current level: ${logger.getLevel()}
    `);
  };
}